import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Field,
  LoadingRegion,
  Notice,
  Status,
} from '@gatecheckpro/design-system';
import { stableCodeSchema, type Venue } from '@gatecheckpro/contracts';
import { useCommand } from '../lib/useCommand.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * One venue: its spaces, its access zones and its gates.
 *
 * The page has to teach one idea without a manual — a code is permanent and a
 * name is not. Renaming a zone from "VIP" to "Platinum" moves a label; the code
 * is what gate policy and ticket entitlements point at, and what a scanner is
 * provisioned against.
 */
export function VenueDetailPage() {
  const { organizationId, venueId } = useParams<{ organizationId: string; venueId: string }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}/venues/${venueId ?? ''}`;
  const { resource, reload } = useResource<Venue>(base);
  const command = useCommand<Venue>();

  const [zoneCode, setZoneCode] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [zoneKind, setZoneKind] = useState('GENERAL');
  const [gateCode, setGateCode] = useState('');
  const [gateName, setGateName] = useState('');
  const [gateAccessible, setGateAccessible] = useState(false);
  const [spaceName, setSpaceName] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);

  if (resource.status === 'loading') return <LoadingRegion label="Loading venue" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const venue = resource.data;
  const zoneCodeProblem =
    zoneCode === '' || stableCodeSchema.safeParse(zoneCode).success
      ? undefined
      : 'Capitals, numbers and underscores, starting with a letter.';
  const gateCodeProblem =
    gateCode === '' || stableCodeSchema.safeParse(gateCode).success
      ? undefined
      : 'Capitals, numbers and underscores, starting with a letter.';

  const after = (result: Venue | null): void => {
    if (result === null) return;
    setZoneCode('');
    setZoneName('');
    setGateCode('');
    setGateName('');
    setSpaceName('');
    setRenaming(null);
    reload();
  };

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to={`/o/${organizationId ?? ''}/venues`}>Venues</Link>
        </span>
        <h2>{venue.name}</h2>
        <p className="gc-muted">
          {venue.publicLocality} · {venue.countryCode} · {venue.timezone}
        </p>
      </div>

      <FailureNotice error={command.error} />

      <Notice tone="info" title="Codes are permanent, names are not">
        A zone or gate code is what gate policy, ticket entitlements and scanner provisioning
        reference. You can rename a zone whenever you like; you cannot re-code one.
      </Notice>

      {/* --- Zones --- */}
      <Card title="Access zones" eyebrow="Areas a ticket can admit to">
        <div className="gc-stack">
          {venue.zones.length === 0 ? (
            <EmptyState title="No zones yet">
              Every ticket admits to a zone. Most venues need at least a general admission area.
            </EmptyState>
          ) : (
            <div className="gc-table-scroll">
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Kind</th>
                    <th scope="col">
                      <span className="gc-visually-hidden">Rename</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {venue.zones.map((zone) => (
                    <tr key={zone.accessZoneId}>
                      <td className="gc-mono">{zone.code}</td>
                      <td>
                        {renaming?.id === zone.accessZoneId ? (
                          <input
                            className="gc-input"
                            value={renaming.value}
                            onChange={(input) =>
                              setRenaming({ id: zone.accessZoneId, value: input.target.value })
                            }
                          />
                        ) : (
                          zone.displayName
                        )}
                      </td>
                      <td className="gc-muted">{zone.kind}</td>
                      <td>
                        {renaming?.id === zone.accessZoneId ? (
                          <div className="gc-row">
                            <Button
                              variant="primary"
                              busy={command.busy}
                              onClick={() =>
                                void command
                                  .run(`${base}/zones/${zone.accessZoneId}`, {
                                    method: 'PUT',
                                    body: { displayName: renaming.value },
                                  })
                                  .then(after)
                              }
                            >
                              Save
                            </Button>
                            <Button onClick={() => setRenaming(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <Button
                            onClick={() =>
                              setRenaming({ id: zone.accessZoneId, value: zone.displayName })
                            }
                          >
                            Rename
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form
            className="gc-stack"
            onSubmit={(formEvent) => {
              formEvent.preventDefault();
              void command
                .run(`${base}/zones`, {
                  method: 'POST',
                  body: { code: zoneCode, displayName: zoneName, kind: zoneKind },
                })
                .then(after);
            }}
          >
            <div className="gc-grid">
              <Field
                label="Code"
                placeholder="VIP"
                required
                error={zoneCodeProblem}
                hint="Permanent."
                value={zoneCode}
                onChange={(input) => setZoneCode(input.target.value.toUpperCase())}
              />
              <Field
                label="Name"
                placeholder="VIP lounge"
                required
                hint="What people read. Change it whenever."
                value={zoneName}
                onChange={(input) => setZoneName(input.target.value)}
              />
              <div className="gc-field">
                <label className="gc-field__label" htmlFor="zoneKind">
                  Kind
                </label>
                <select
                  id="zoneKind"
                  className="gc-select"
                  value={zoneKind}
                  onChange={(input) => setZoneKind(input.target.value)}
                >
                  <option value="GENERAL">General — standard admission areas</option>
                  <option value="PREMIUM">Premium — VIP, hospitality</option>
                  <option value="RESTRICTED">Restricted — backstage, production</option>
                  <option value="OPERATIONAL">Operational — crew, vendors, media</option>
                </select>
              </div>
            </div>
            <Button
              type="submit"
              variant="primary"
              busy={command.busy}
              disabled={zoneCode === '' || zoneName === '' || zoneCodeProblem !== undefined}
            >
              Add zone
            </Button>
          </form>
        </div>
      </Card>

      {/* --- Gates --- */}
      <Card title="Gates" eyebrow="Entrances">
        <div className="gc-stack">
          {venue.gates.length === 0 ? (
            <EmptyState title="No gates yet">
              A gate is a door people come through. Each event then decides which zones that door
              admits to on the night.
            </EmptyState>
          ) : (
            <div className="gc-table-scroll">
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Accessibility</th>
                  </tr>
                </thead>
                <tbody>
                  {venue.gates.map((gate) => (
                    <tr key={gate.gateId}>
                      <td className="gc-mono">{gate.code}</td>
                      <td>{gate.displayName}</td>
                      <td>
                        {gate.isAccessibleEntry ? (
                          <Status tone="info" glyph="♿" label="Accessible entry" />
                        ) : (
                          <span className="gc-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form
            className="gc-stack"
            onSubmit={(formEvent) => {
              formEvent.preventDefault();
              void command
                .run(`${base}/gates`, {
                  method: 'POST',
                  body: {
                    code: gateCode,
                    displayName: gateName,
                    isAccessibleEntry: gateAccessible,
                    internalNotes: null,
                  },
                })
                .then(after);
            }}
          >
            <div className="gc-grid">
              <Field
                label="Code"
                placeholder="MAIN"
                required
                error={gateCodeProblem}
                hint="Permanent. A scanner will be provisioned against it."
                value={gateCode}
                onChange={(input) => setGateCode(input.target.value.toUpperCase())}
              />
              <Field
                label="Name"
                placeholder="Main entrance"
                required
                value={gateName}
                onChange={(input) => setGateName(input.target.value)}
              />
            </div>
            <label className="gc-checkbox">
              <input
                type="checkbox"
                checked={gateAccessible}
                onChange={(input) => setGateAccessible(input.target.checked)}
              />
              <span className="gc-checkbox__body">
                <span className="gc-checkbox__title">Step-free accessible entry</span>
                <span className="gc-muted">
                  Used to route guests who need it. Not shown publicly in this release.
                </span>
              </span>
            </label>
            <Button
              type="submit"
              variant="primary"
              busy={command.busy}
              disabled={gateCode === '' || gateName === '' || gateCodeProblem !== undefined}
            >
              Add gate
            </Button>
          </form>
        </div>
      </Card>

      {/* --- Maps --- */}
      <Card title="Maps" eyebrow="The shape of a room">
        <div className="gc-stack gc-stack--tight">
          <p className="gc-muted">
            Sections, rows, seats and tables, drawn once per room and reused by every event held
            here. A map says what the room is; what is sold in it is configured per event.
          </p>
          <Link to={`/o/${organizationId ?? ''}/venues/${venueId ?? ''}/maps`}>Open maps</Link>
        </div>
      </Card>

      {/* --- Spaces --- */}
      <Card title="Spaces" eyebrow="Parts of the building">
        <div className="gc-stack">
          {venue.spaces.length === 0 ? (
            <p className="gc-muted">
              Optional. Useful when a venue has distinct areas whose physical occupancy differs — a
              floor and a balcony.
            </p>
          ) : (
            <ul className="gc-stack gc-stack--tight" style={{ paddingInlineStart: '1.1rem' }}>
              {venue.spaces.map((space) => (
                <li key={space.venueSpaceId}>
                  {space.name}
                  {space.physicalCapacity !== null && (
                    <span className="gc-muted">
                      {' '}
                      — holds {space.physicalCapacity.toLocaleString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <form
            className="gc-row"
            onSubmit={(formEvent) => {
              formEvent.preventDefault();
              void command
                .run(`${base}/spaces`, {
                  method: 'POST',
                  body: { name: spaceName, physicalCapacity: null },
                })
                .then(after);
            }}
          >
            <div style={{ flex: '1 1 16rem' }}>
              <Field
                label="Space name"
                placeholder="Main floor"
                value={spaceName}
                onChange={(input) => setSpaceName(input.target.value)}
              />
            </div>
            <Button type="submit" busy={command.busy} disabled={spaceName === ''}>
              Add space
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
